import { AlertCircle, Eye } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { FaceActionErrorKey } from '@/features/face-matching/errors'
import { cn } from '@/utils/cn'

/** Why a viewer is offered no decision about a face: one sentence for every face surface. */
const READ_ONLY = 'cases.investigate.entityResolution.photoMatching.readOnly'

export interface FaceActionNoticeProps {
  /** The surface's i18n prefix: its refusals are worded under `${copy}.${errorKey}`. */
  copy: string
  /** The last action's refusal; null when it went through. */
  errorKey: FaceActionErrorKey | null
  /** The caller only views the case, and a decision would have been offered here. */
  readOnly: boolean
  /** The size of a narrow column — the overlay's rail. */
  compact?: boolean
  /** The viewer's sentence; the face surfaces' one unless the surface has its own (voices). */
  readOnlyKey?: string
}

/**
 * What a face or voice surface says above its rows: the refusal of the last action, in
 * the surface's own words — else, to a viewer, why it offers no decision
 * (PRD-0032 SC-036). Never both: after a refused click the refusal keeps
 * speaking, and the viewer's line would only repeat it.
 */
export function FaceActionNotice({
  copy,
  errorKey,
  readOnly,
  compact = false,
  readOnlyKey = READ_ONLY,
}: FaceActionNoticeProps) {
  const { t } = useTranslation()
  const size = compact ? 'text-xs' : 'text-sm'
  if (errorKey) {
    return (
      <p
        role="alert"
        data-face-decision-error={errorKey}
        className={cn(
          'flex items-start gap-2 rounded-md border border-destructive-border bg-destructive-surface py-2 text-destructive-foreground',
          compact ? 'px-2' : 'px-3',
          size
        )}
      >
        <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
        <span>{t(`${copy}.${errorKey}`)}</span>
      </p>
    )
  }
  if (!readOnly) return null
  return (
    <p role="note" className={cn('flex items-start gap-2 text-muted-foreground', size)}>
      <Eye className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span>{t(readOnlyKey)}</span>
    </p>
  )
}
